import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { Divider, Space } from 'antd';
import CheckContainer from 'components/CheckContainer';
import usePromoteDays, { PROMOTE_I18N_NS } from 'hooks/business/usePromoteDays';
import { List, PanelTitle, ListItem, PackTip } from './Common';
import RecomendTag from './RecomendTag';
import { useMemo } from 'react';
import { getCurrencySymbol } from 'components/Currency';
import { formatNumber } from 'utils/numberFormat';
import { isQuickChat } from 'productLine';
import { useTranslate } from '@tolgee/react';
import { useMemoizedFn } from 'ahooks';
import { isNil } from 'lodash-es';
import { getSubscriptionBillingCycleLabel } from 'services/i18n/enum/subscription';

export interface DurationSelectPanelProps {
  durationId: Pay.DurationId;
  packageId?: Pay.PackageId;
  currency: General.Currency;
  orderType?: Pay.OrderType;
  recommendDurationId?: Pay.DurationId;
  // 是否可以增加时长
  canIncreaseDuration?: boolean;
  list: Pay.DurationDescV2[];
  disabled?: (value: Pay.DurationDescV2) => boolean;
  onChange?: (value: Pay.DurationDescV2) => void;
  packageData?: Pay.PackageV2[];
}

export const DurationSelectPanel = (props: DurationSelectPanelProps) => {
  const {
    durationId,
    packageId,
    currency,
    orderType,
    recommendDurationId,
    canIncreaseDuration,
    list,
    disabled,
    onChange,
    packageData,
  } = props;
  const { t } = useTranslate();
  const { inPromoteRange } = usePromoteDays();

  // 各个套餐的赠送时长数据
  const packageExtraDurationNumMap = useMemo(() => {
    const map: Record<string, number> = {};

    packageData?.forEach(({ id: packageId, durationDesc }) => {
      durationDesc.forEach(({ id, extraDurationNum }) => {
        map[`${packageId}_${id}`] = extraDurationNum ?? 0;
      });
    });

    return map;
  }, [packageData]);

  // 立减优惠映射关系
  const packageReducePriceMap = useMemo(() => {
    const map: Record<string, number> = {};

    packageData?.forEach(({ id: packageId, durationDesc }) => {
      durationDesc.forEach(({ id, currencyAmount }) => {
        currencyAmount.forEach(({ currency, reducePrice }) => {
          map[`${packageId}_${id}_${currency}`] = reducePrice ?? 0;
        });
      });
    });

    return map;
  }, [packageData]);

  const renderPackageDuration = useMemoizedFn(
    (pkg: Pay.DurationDescV2, showSpecialIcon: boolean) => {
      const { id, durationNum } = pkg;

      if (showSpecialIcon) {
        // 当前套餐赠送时长
        const extraDuration = packageExtraDurationNumMap[`${packageId}_${id}`];
        // 当前套餐立减优惠价格
        const reducePrice = packageReducePriceMap[`${packageId}_${id}_${currency}`];

        const getDefaultValue = () => {
          if (extraDuration) {
            if (inPromoteRange) {
              return t({
                ns: PROMOTE_I18N_NS,
                key: 'promote.extra.deal',
                params: { nums: extraDuration },
              });
            } else {
              return t('market.extra.deal', {
                count: extraDuration,
              });
            }
          }

          if (reducePrice) {
            return t({
              key: 'market_package_reduce_price_tips',
              params: {
                currency: getCurrencySymbol(currency),
                price: formatNumber(reducePrice, '0,0'),
              },
            });
          }
        };

        return (
          <PackTips>
            {t({
              ns: PROMOTE_I18N_NS,
              key: `promote.package.${packageId}.tag.duration.${durationNum}.desc`,
              defaultValue: getDefaultValue() ?? '',
            })}
          </PackTips>
        );
      }

      return null;
    },
  );

  return (
    <div>
      <PanelTitle>
        {orderType === 'UPGRADE' ? (
          <>
            {t('market.增加套餐时长')}
            <Divider type="vertical" />
            <PackTip>
              {canIncreaseDuration ? t('market.增加时长优惠') : t('market.升级套餐不可增加时长')}
            </PackTip>
          </>
        ) : (
          t('scene.managementCenter.sellerPlan.subscriptionTab.label.selectBillingCycle')
        )}
      </PanelTitle>
      <List>
        {list.map((item) => {
          const { id, durationNum, iconText, promotionType } = item;
          const checked = id === durationId;
          const isDisabled = disabled?.(item);
          const showSpecialIcon =
            !!iconText && inPromoteRange && !isNil(packageId) && !isNil(durationNum);

          const durationLabel = getSubscriptionBillingCycleLabel(t, durationNum);

          return (
            <ListItem key={id} className={isQuickChat ? '!basis-[220px] !flex-grow-0' : ''}>
              <CheckItem
                disabled={isDisabled}
                checked={checked}
                onChange={() => {
                  if (checked) return;

                  onChange?.(item);
                }}
              >
                <Space size={4}>
                  {durationLabel}
                  {promotionType && (
                    <span className={promotionTag}>{t(`promotionType.${promotionType}`)}</span>
                  )}
                </Space>
                {renderPackageDuration(item, showSpecialIcon)}
              </CheckItem>
              {showSpecialIcon ? (
                <RecomendTag
                  // 通过后端接口配置返回，本地ts解释有问题，因此进行ignore @haofeng.zhao
                  text={t({
                    ns: PROMOTE_I18N_NS,
                    key: `promote.package.${packageId}.tag.duration.${durationNum}.text`,
                    defaultValue: '',
                  })}
                />
              ) : (
                recommendDurationId === id && <RecomendTag />
              )}
            </ListItem>
          );
        })}
      </List>
    </div>
  );
};

const CheckItem = styled(CheckContainer)`
  min-width: 80px;
  min-height: 76px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0 16px;
  border-radius: 2px;
`;
const PackTips = styled.div`
  margin-top: 4px;
  font-weight: 400;
  font-size: 12px;
  color: #f05b5b;
`;

const promotionTag = css`
  padding: 2px 8px;
  background-image: linear-gradient(136deg, #ffa573 0%, #f84815 100%);
  border-radius: 7px;
  font-size: 12px;
  color: #ffffff;
`;
